import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { FinancialDatasetRequest } from '@futrli-sdk/predict';
import { getErrorStatus, ERROR_STATUS } from '@futrli/errors';

import { getReportBulkLiveData } from '../core/api/reporting/report-builder';
import { FinData } from './types';
import { convertFinDataToIncludeForecastId } from './utils-data';
import { transformFinancialDataQueriesForV2 } from './utils-queries';
import { usePredictManagementContext } from '../predict-management/context/predict-management-context';

import { GET_FIN_DATA_DELAY_TIME } from './constants';
import { usePool } from '@futrli/shared-services';

type LayoutFinData = Record<string, Record<string, FinData> | undefined>;

export const useWidgetDataReload = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean,
  setLayoutFinData: (data: LayoutFinData) => void,
  layoutFinDataRef: MutableRefObject<LayoutFinData>,
  setIsFetchingFinData: (data: boolean) => void,
  reportId: string,
  isWorkspaceTemplate: boolean
) => {
  const { addErrorMessage, financialConfig } = usePredictManagementContext();

  const { addToPool, activeItemsRef, itemIds, poolRef, setActiveItem, removeActiveItem } = usePool<
    Record<string, FinancialDatasetRequest>
  >();
  const mounted = useRef(true);
  const timerIds = useRef<Record<string, number>>({});

  const loadWidgetFinData = useCallback(
    async (id: string) => {
      if (financialConfig) {
        const { data: query } = poolRef.current[id];
        try {
          setIsFetchingFinData(true);
          setActiveItem(id);
          const data = await getReportBulkLiveData(
            workspaceId,
            organisationId,
            transformFinancialDataQueriesForV2(query),
            isConsolidation,
            reportId,
            isWorkspaceTemplate
          );

          // Check if our query is the latest one in active items (if not, another later request has been made)
          if (activeItemsRef.current[id]?.data === query && mounted.current) {
            const newData = convertFinDataToIncludeForecastId(data);
            setLayoutFinData({ ...layoutFinDataRef.current, ...newData });
          }
        } catch (error) {
          if (mounted.current && getErrorStatus(error) !== ERROR_STATUS._404) {
            addErrorMessage?.(new Error('Failure to fetch live Financial Data'));
          }
        } finally {
          if (mounted.current) {
            removeActiveItem(id);
            setIsFetchingFinData(!!Object.keys(activeItemsRef.current).length);
          }
        }
      }
    },
    [
      activeItemsRef,
      addErrorMessage,
      financialConfig,
      isConsolidation,
      isWorkspaceTemplate,
      layoutFinDataRef,
      organisationId,
      poolRef,
      removeActiveItem,
      reportId,
      setActiveItem,
      setIsFetchingFinData,
      setLayoutFinData,
      workspaceId
    ]
  );

  // If an item is added to the queue, debounce before loading data
  const addToWidgetPool = useCallback(
    (id: string, data: Record<string, FinancialDatasetRequest>) => {
      if (timerIds.current[id]) {
        clearTimeout(timerIds.current[id]);
        delete timerIds.current[id];
      }
      addToPool(id, data);
      timerIds.current[id] = window.setTimeout(() => {
        loadWidgetFinData(id);
      }, GET_FIN_DATA_DELAY_TIME);
    },
    [addToPool, loadWidgetFinData]
  );

  // Upon unmount, cancel any outstanding timers
  useEffect(
    () => () => {
      mounted.current = false;
      Object.values(timerIds.current).forEach(timerId => {
        clearTimeout(timerId);
      });
    },
    []
  );

  return [addToWidgetPool, itemIds] as const;
};
